import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import classnames from "classnames"
import If from "@mobi/components/If"
import Text from "../Text"
import Radio from "../Radio"
import ChipList from "../Chips"
import TextArea from "../TextArea"
import Button from "../Button"
import "./Likert.scss"
import { IChipProps } from "../Chips/components/types"
import isClient from "@mobi/utils/browser/isClient"

import IconBad from "../../static/icons/likert/bad.svg"
import IconDislike from "../../static/icons/likert/dislike.svg"
import IconIdk from "../../static/icons/likert/idk.svg"
import IconLike from "../../static/icons/likert/like.svg"
import IconLoved from "../../static/icons/likert/loved.svg"
import IconBrilho from "../../static/icons/outlined/brilho-nos-olhos.svg"

type TDataCallback = {
  experience: string
  options: string
  description: string
}
type TExperienceClickCallback = {
  experience: string
}
type TOptionsClickCallback = {
  option: string
}

interface Likert {
  /** Callback com data do formulário, irá retornar um objeto {experience, options, description}, todos strings */
  dataCallback?: (data: TDataCallback) => TDataCallback
  /** Callback do clique do experience, irá retornar um objeto {experience} */
  experienceClickCallback?: (
    data: TExperienceClickCallback
  ) => TExperienceClickCallback
  /** Callback do clique do options, irá retornar um objeto {experience} */
  optionsClickCallback?: (data: TOptionsClickCallback) => TOptionsClickCallback
  /** Limite de carecteres do campo de texto */
  maxLength?:number
  /** Lista de opções de feedback opcionais que aparecerá após o usuário selecionar uma das opções de experiência. */
  options: IChipProps[]
  /** Flag para escolher se quer que o foco seja direcionado para o texto de feedback após envio das informações */
  focusOnFeedbackText?: boolean
  /** Título do Likert */
  likertTitle?: string
  /** Tipo de tag que o título deve ser renderizada */
  likertTitleTag?: string
  /** Flag  que determina se o formulário apenas será enviado ao clicar no botão*/
  submitOnClick?: boolean
}

const Likert = ({
  options,
  dataCallback,
  experienceClickCallback,
  optionsClickCallback,
  focusOnFeedbackText = true,
  likertTitle = "o que achou dessa experiência?",
  likertTitleTag = "p",
  maxLength = 140,
  submitOnClick = false
}: Likert) => {
  if (!isClient()) return null  
  const likertBaseClass = "ds-likert"
  const LikertClasses = classnames(likertBaseClass)
  const [selectedExp, setSelectedExp] = useState("")
  const [realTimeLength, setRealTimeLength] = useState(maxLength)
  const wasRated = !!sessionStorage.getItem("has-send")
  const [hasSend, setHasSend] = useState(wasRated)
  const [question, setQuestion] = useState("descreva aqui (opcional)")
  

  const { control, handleSubmit } = useForm({
    mode: "onTouched",
    defaultValues: {},
  })

  const optionsWithGroup = options?.map((opt) => {
    return { ...opt, name: "options" }
  })

  const setFocusOnFeedbackText = () => {
    setTimeout(() => {
      const text = document.querySelector("#ds-likert-feedback-text")
      if (text) {
        text.setAttribute("tabindex", "0")
        text.focus()
        setTimeout(() => {
          text.removeAttribute("tabindex")
        }, 200)
      }
    }, 100)
  }

  const onSubmit = (data) => {
    sessionStorage.setItem("has-send", !hasSend)
    setHasSend(true)

    if (focusOnFeedbackText) setFocusOnFeedbackText()

    dataCallback?.({
      experience: data.experience,
      options: data.options === undefined ? "" : data.options,
      description: data.description === undefined ? "" : data.description,
    })
  }

  const handleExperienceClick = (experience, relatedQuestion) => {
    if (!optionsWithGroup) {
      setQuestion(relatedQuestion)
    }
    setSelectedExp(experience)
    experienceClickCallback?.({ experience })
  }

  const handleOptionsClick = ({ option }) => {
    optionsClickCallback?.({ option })
  }

  const handleMaxLengthLabel = (value) => {
    setRealTimeLength(maxLength - value.length)
  }

  const likertExperiences = [
    {
      icon: <IconBad />,
      text: "muito ruim",
      value: "BAD",
      relatedQuestion: "o que poderia melhorar? (opcional)",
    },
    {
      icon: <IconDislike />,
      text: "ruim",
      value: "NOT_SATISFIED",
      relatedQuestion: "o que poderia melhorar? (opcional)",
    },
    {
      icon: <IconIdk />,
      text: "regular",
      value: "NOT_SURE",
      relatedQuestion: "o que gostou? (opcional)",
    },
    {
      icon: <IconLike />,
      text: "bom",
      value: "LIKED",
      relatedQuestion: "o que gostou? (opcional)",
    },
    {
      icon: <IconLoved />,
      text: "muito bom",
      value: "LOVED",
      relatedQuestion: "o que gostou? (opcional)",
    },
  ]

  const renderExperiences = likertExperiences.map(
    ({ icon, text, value, relatedQuestion, ariaLabel = "" }, index) => {
      return (
        <Radio

          key={index}
          className={`${likertBaseClass}__radio-item ${
            selectedExp === value
              ? `${likertBaseClass}__radio-item--selected`
              : ""
          }`}
          control={control}
          id={value}
          name="experience"
          value={value}
          ariaLabel={ariaLabel || text}
          onClick={() => handleExperienceClick(value, relatedQuestion)}
        >
          <div className={`${likertBaseClass}__label-container`} aria-hidden>
            <span aria-hidden="true">{icon}</span>
            <span className={`${likertBaseClass}__text`}>{text}</span>
          </div>
        </Radio>
      )
    }
  )

  return (
    <form onSubmit={!submitOnClick && handleSubmit(onSubmit)} className={`${LikertClasses}`}>
      <If
        condition={!hasSend}
        renderIf={
          <>
            <div className={`${likertBaseClass}__container--experiences`}>
              <Text
                as={likertTitleTag}
                size="md"
                weight={400}
                className={`${likertBaseClass}__title`}
              >
                {likertTitle}
              </Text>
              <div
                role="radiogroup"
                aria-label={likertTitle}
                className={`${likertBaseClass}__experiences`}
              >
                {renderExperiences}
              </div>
            </div>

            {/* OPTIONS */}
            <If
              condition={selectedExp !== ""}
              renderIf={
                <>
                  <If
                    condition={optionsWithGroup?.length > 0}
                    renderIf={
                      <div className={`${likertBaseClass}__container`}>
                        <Text size="md" weight={400}>
                        Selecione a opção que mais motivou sua resposta anterior:
                        </Text>
                        <ChipList
                          listStyle="likert"
                          list={optionsWithGroup}
                          control={control}
                          clickCallback={handleOptionsClick}
                          modifier='likert'
                        />
                      </div>
                    }
                  />
                  <div className={`${likertBaseClass}__container ${likertBaseClass}__container--no-title`}>
                    <Text size="md" weight={400}>
                      {question}
                    </Text>
                    <TextArea
                      label="descreva aqui (opcional)"
                      maxLength={maxLength}
                      realTimeLength={realTimeLength}
                      name="description"
                      control={control}
                      onKeyUp={(e) => handleMaxLengthLabel(e.target.value)}
                      placeholder='O que podemos melhorar?'
                    />
                    <Button variant="primary" onClick={submitOnClick && handleSubmit(onSubmit)} type={submitOnClick ? "button" : "submit"}>
                      enviar
                    </Button>
                  </div>
                </>
              }
            />
          </>
        }
        renderElse={
          <>
            <div className={`${likertBaseClass}__container--send`}>
              <IconBrilho aria-hidden />
              <Text size="md" weight={700} id="ds-likert-feedback-text">
                obrigado por dedicar o seu tempo
              </Text>
              <Text size="sm" weight={400}>
                estamos sempre trabalhando para melhorar sua experiência.
              </Text>
            </div>
          </>
        }
      />
    </form>
  )
}

export default Likert
